import React, { useEffect, useRef } from "react";
import { Checkbox } from "flowbite-react";
import { motion, AnimatePresence } from "framer-motion";
import { CaretDown } from "@phosphor-icons/react";

interface AnimatedAccordionProps {
  title: React.ReactNode;
  checked: boolean;
  indeterminate?: boolean;
  onToggleAll: (checked: boolean) => void;
  expanded: boolean;
  onToggleExpand: () => void;
  children: React.ReactNode;
  className?: string;
  checkBoxColor?: string;
}

const AnimatedAccordion: React.FC<AnimatedAccordionProps> = ({
  title,
  checked,
  indeterminate = false,
  onToggleAll,
  expanded,
  onToggleExpand,
  children,
  className = "",
  checkBoxColor = "indigo",
}) => {
  const checkboxRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <div className={`border border-gray-200 dark:border-neutral-700 rounded-lg overflow-hidden bg-white dark:bg-neutral-900 ${className}`}>
      <button
        type="button"
        onClick={onToggleExpand}
        className="w-full px-4 py-2 flex items-center justify-between bg-gray-50 dark:bg-neutral-800 hover:bg-gray-100 dark:hover:bg-neutral-700 transition-colors"
      >
        <div className="flex items-center space-x-2">
          <Checkbox
            color={checkBoxColor}
            ref={checkboxRef}
            checked={checked}
            onChange={(e) => onToggleAll(e.target.checked)}
            className="mr-2"
            onClick={(e) => e.stopPropagation()}
          />
          <span className="font-medium select-none">{title}</span>
        </div>
        <motion.div
          animate={{ rotate: expanded ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="cursor-pointer"
        >
          <CaretDown className="w-4 h-4" />
        </motion.div>
      </button>
      <AnimatePresence initial={false} mode="sync">
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="p-4">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AnimatedAccordion;
